import { Stomp } from "@stomp/stompjs";
import { setExtendableId, setExtendableType } from "./edit";
import {
  setDeterminateLoading,
  setInitialProjectLoading,
  setLoadingProject
} from "./loading";
import { resetProject, setCurrentWindow, setSelectedRoutes } from "./project";
import API from "../../utils/axios";
import { PROJECT, SOLUTION } from "./types";
import { enqueueSnackbar } from "./snackbars";
import {
  checkFor401,
  getFormattedRoutes,
  getRouteColors
} from "./actionHelpers";
import history from "../../utils/history";
import { WS_API_URL } from "../../components/data/constants";
import { setRefitHash } from "./map";
import uuidv4 from "uuid/v4";

export let stompClient = null;
let solutionInterval;

const isFinished = status => status?.toLowerCase() === "finished";
const isError = status => status?.toLowerCase() === "error";

export function setSolution(obj) {
  return (dispatch, getState) => {
    const vehicles = getState().project.vehicles;
    if (obj) {
      obj.routeGroups = getFormattedRoutes(
        obj.routes,
        obj.showAll ? [] : vehicles
      );
      obj.routeColors = getRouteColors(obj.routeGroups);
    }

    dispatch({
      type: SOLUTION,
      obj
    });
  };
}

// Start interval for getting optimize state and solution by project ID
export function setSolutionByCallback(id, postOptimize, opts, callback) {
  return function (dispatch) {
    if (sessionStorage.currentProject !== id) return;
    dispatch(setExtendableType(null));
    dispatch(setExtendableId(null));

    // dispatch(setDeterminateLoading({ loading: true }));
    dispatch(setLoadingProject(true));

    dispatch(connectStomp(id, postOptimize, opts));

    dispatch(
      setSolutionById(id, true, { ...opts, asFallback: postOptimize }, callback)
    );
  };
}

const connectStomp = (id, postOptimize, opts) => (dispatch, getState) => {
  let apiUrl = WS_API_URL;

  var sock = new WebSocket(apiUrl + "/v1/ws");
  stompClient = Stomp.over(sock);

  stompClient.connect(
    {},
    function (frame) {
      stompClient.subscribe(
        `/organisation/${
          getState().auth.userData.organisationSite.organisation.id
        }/v1/ws/roe/status`,
        function (messageOutput) {
          const data = JSON.parse(messageOutput.body);
          dispatch(
            handleWSData(id, opts, data, () => {
              console.log("disconnecting");
              stompClient && stompClient.disconnect();
            })
          );
        }
      );
    },
    function () {
      console.log("disconnect 2");
      setSolutionById(id, postOptimize, opts);
    }
  );
};

let handleWSData = (id, opts, data, fallbackAction) => dispatch => {
  if (id !== data.project_id || sessionStorage.currentProject !== id) return;

  if (
    !isFinished(data.callback_token_status) &&
    !isError(data.callback_token_status)
  ) {
    dispatch(
      setDeterminateLoading({
        loading: true,
        percent: data.optimization_percent,
        status: data.callback_token_status
      })
    );
  }

  if (
    isFinished(data.callback_token_status) ||
    data.optimization_percent === 100
  ) {
    dispatch(
      setDeterminateLoading({
        loading: false,
        percent: data.optimization_percent,
        status: data.callback_token_status
      })
    );
    fallbackAction();
    dispatch(setSolutionById(id, true, opts));
  }

  if (!data || isError(data.callback_token_status)) {
    fallbackAction();
    dispatch(setLoadingProject(false));
    dispatch(setSelectedRoutes([]));
    dispatch(
      setDeterminateLoading({
        loading: false,
        percent: data.optimization_percent,
        status: data.callback_token_status
      })
    );
    dispatch(setSolutionById(id, true, opts));
  }
};

export const handleSolution =
  (solution, postOptimize, options) => (dispatch, getState) => {
    const projectStatus = getState().project.status || "";

    if (isFinished(solution.status)) {
      stompClient && stompClient.disconnect();
      const selectedVehicles = getState().project.selectedVehicles;
      if (
        (solution.routes.some(route =>
          selectedVehicles.includes(route.vehicle_id)
        ) &&
          (projectStatus.toLowerCase() !== "preparing" || postOptimize)) ||
        (getState().project.solution &&
          getState().project.solution.status !== "FINISHED")
      ) {
        dispatch(setCurrentWindow("Optimized"));
      } else {
        dispatch(setCurrentWindow("Project"));
      }

      if (
        projectStatus === "created" ||
        (postOptimize && projectStatus === "preparing") ||
        (getState().project.solution &&
          getState().project.solution.length &&
          getState().project.solution.status !== "FINISHED")
      ) {
        dispatch({
          type: PROJECT,
          obj: {
            ...getState().project,
            status: "optimized"
          }
        });
      }

      dispatch(
        setDeterminateLoading({
          loading: false,
          percent: 0,
          status: ""
        })
      );

      dispatch(setSolution(solution));

      dispatch(setRefitHash(uuidv4()));

      // if (
      //   solution.unassigned.stops.length &&
      //   projectStatus &&
      //   !["published", "expired", "finished"].includes(
      //     projectStatus.toLowerCase()
      //   ) &&
      //   solution.id === sessionStorage.currentProject
      // ) {
      //   dispatch(setExtendableType("unscheduled"));
      // } else {
      //   dispatch(setExtendableType(null));
      // }
    }

    if (!solution || isFinished(solution.status) || isError(solution.status)) {
      stompClient && stompClient.disconnect();
      dispatch(
        setDeterminateLoading({
          loading: false,
          percent: 0,
          status: ""
        })
      );
      clearInterval(solutionInterval);
      dispatch(setLoadingProject(false));
      if (options.initialLoading) {
        dispatch(setInitialProjectLoading(false));
      }
      dispatch(setSelectedRoutes([]));
      if (solution.status === "ERROR") {
        dispatch(setCurrentWindow("Project"));
        if (postOptimize) {
          dispatch(
            enqueueSnackbar({
              message:
                "Failed to optimize. If the problem persists, please contact support."
            })
          );
        }
      }
    }
  };

// Start interval for getting solution by project ID
export function setSolutionById(
  id,
  postOptimize = false,
  options = {},
  callback
) {
  solutionInterval && clearInterval(solutionInterval);
  return function (dispatch, getState) {
    if (sessionStorage.currentProject !== id) return;

    let gotResponse = false;

    const getData = () => {
      dispatch(setLoadingProject(true));
      if (options.initialLoading) {
        dispatch(setInitialProjectLoading(true));
      }

      API.get(`/v1/projects/${id}/solutions`)
        .then(res => {
          gotResponse = true;
          if (sessionStorage.currentProject !== id) return;
          callback && callback(undefined, res.data);
          dispatch(handleSolution(res.data, postOptimize, options));
        })
        .catch(error => {
          // cleanup
          clearInterval(solutionInterval);
          dispatch(setLoadingProject(false));
          dispatch(setInitialProjectLoading(false));
          stompClient && stompClient.disconnect();
          dispatch(setSelectedRoutes([]));
          dispatch(
            setDeterminateLoading({
              loading: false,
              percent: 0,
              status: ""
            })
          );
          gotResponse = true;
          callback && callback(error);
          // handle error
          if (!checkFor401(error)) {
            dispatch(
              enqueueSnackbar({
                message:
                  "Failed to fetch data. If the problem persists, please contact us.",
                style: { whiteSpace: "pre-line" }
              })
            );
          } else {
            if (!getState().project.solution) {
              history.push("/");
              dispatch(resetProject());
            }
          }
        });
    };

    const startInterval = timeout => {
      const interval = setInterval(() => {
        if (getState().project.id === id) {
          if (gotResponse) {
            gotResponse = false;
            getData();
          }
        } else {
          clearInterval(interval);
        }
      }, timeout);

      return interval;
    };

    getData();
    solutionInterval = startInterval(options.asFallback ? 10000 : 3000);
  };
}
